import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoices } from '../api/methods';
import { salesKeys } from './useSales';
export const invoiceKeys = {
    all: ['invoices'],
    drafts: ['invoices', 'drafts'],
    draft: (id) => ['invoices', 'draft', id],
};
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function stringValue(value) {
    if (typeof value === 'string')
        return value.trim();
    if (typeof value === 'number' && Number.isFinite(value))
        return String(value);
    return '';
}
/**
 * Translate every convert-refusal the backend can raise (#567 added two codes
 * beyond the original already-invoiced 409). Name kept for existing call sites.
 */
export function getEstimateAlreadyInvoicedMessage(error) {
    if (!isRecord(error) || !isRecord(error.data))
        return null;
    if (error.data.error === 'estimate_linked_to_ticket') {
        return 'This estimate is linked to a field ticket — bill it through the ticket pipeline instead.';
    }
    if (error.data.error === 'estimate_status_unverified') {
        return 'Could not verify this estimate against QuickBooks. Nothing was invoiced — try again.';
    }
    if (error.data.error !== 'estimate_already_invoiced')
        return null;
    const invoice = isRecord(error.data.invoice) ? error.data.invoice : {};
    const invoiceNumber = stringValue(invoice.invoice_number);
    const qboInvoiceId = stringValue(invoice.qbo_invoice_id);
    const label = invoiceNumber ? `#${invoiceNumber}` : qboInvoiceId ? `QBO invoice ${qboInvoiceId}` : 'an invoice';
    return `This estimate has already been invoiced as ${label}.`;
}
/** Whether the invoice builder feature flag is on (gates the builder UI). */
export function useInvoiceBuilderStatus(enabled = true) {
    return useQuery({
        queryKey: ['invoices', 'builder-status'],
        queryFn: () => invoices.builderStatus().then((r) => r.enabled),
        enabled,
        staleTime: 5 * 60_000,
    });
}
/** All invoice drafts for the realm (org-owned; all Managers/Admins see them). */
export function useInvoiceDrafts(enabled = true) {
    return useQuery({
        queryKey: invoiceKeys.drafts,
        queryFn: () => invoices.listDrafts().then((r) => r.drafts),
        enabled,
        staleTime: 15_000,
    });
}
/** A single invoice draft for the builder. */
export function useInvoiceDraft(id, enabled = true) {
    return useQuery({
        queryKey: invoiceKeys.draft(id ?? ''),
        queryFn: () => invoices.getDraft(id).then((r) => r.draft),
        enabled: enabled && !!id,
        staleTime: 5_000,
    });
}
export function useCreateInvoiceDraft() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => invoices.createDraft().then((r) => r.draft),
        onSuccess: () => qc.invalidateQueries({ queryKey: invoiceKeys.drafts }),
    });
}
/** Seed an edit-mode draft prefilled from an existing QBO invoice. */
export function useCreateInvoiceDraftFromInvoice() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (qbInvoiceId) => invoices.createFromInvoice(qbInvoiceId).then((r) => r.draft),
        onSuccess: () => qc.invalidateQueries({ queryKey: invoiceKeys.drafts }),
    });
}
/** Seed a create-mode draft prefilled from an estimate's lines (convert → invoice). */
export function useCreateInvoiceDraftFromEstimate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (qbEstimateId) => invoices.createFromEstimate(qbEstimateId).then((r) => r.draft),
        onSuccess: () => qc.invalidateQueries({ queryKey: invoiceKeys.drafts }),
    });
}
export function useSaveInvoiceDraft() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => invoices.saveDraft(id, data).then((r) => r.draft),
        onSuccess: (draft) => {
            qc.setQueryData(invoiceKeys.draft(draft.id), draft);
            qc.invalidateQueries({ queryKey: invoiceKeys.drafts });
        },
    });
}
export function useDeleteInvoiceDraft() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => invoices.deleteDraft(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: invoiceKeys.drafts }),
    });
}
/**
 * Post a draft to QuickBooks — creates a new invoice or updates an existing one.
 * Invalidates the draft, the draft list, and the sales history caches (the QBO
 * mirror just changed) so every view reflects the new/edited invoice.
 * `overwriteSyncToken` overwrites a concurrent QBO change on an edit-mode draft:
 * the conflict's `current.base_sync_token`, the version the caller showed the
 * user before offering the overwrite.
 */
export function usePostInvoiceDraft() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => {
            if (typeof input === 'string')
                return invoices.postDraft(input, { customerPoId: null });
            return invoices.postDraft(input.id, { overwriteSyncToken: input.overwriteSyncToken, customerPoId: input.customerPoId });
        },
        onSuccess: (_res, input) => {
            const id = typeof input === 'string' ? input : input.id;
            qc.invalidateQueries({ queryKey: invoiceKeys.draft(id) });
            qc.invalidateQueries({ queryKey: invoiceKeys.drafts });
            qc.invalidateQueries({ queryKey: salesKeys.all });
            // Document detail/list caches show stale totals/lines until refetched.
            qc.invalidateQueries({ queryKey: ['sales', 'documents'] });
            qc.invalidateQueries({ queryKey: ['sales', 'document'] });
        },
    });
}
/** Reload an edit draft from the current live invoice (rebase — see api.reloadDraftFromInvoice). */
export function useReloadInvoiceDraftFromInvoice() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => invoices.reloadDraftFromInvoice(id).then((r) => r.draft),
        onSuccess: (draft) => {
            qc.setQueryData(invoiceKeys.draft(draft.id), draft);
            qc.invalidateQueries({ queryKey: invoiceKeys.drafts });
        },
    });
}
//# sourceMappingURL=useInvoices.js.map